import moment from 'moment';
import memoize from 'lodash/memoize.js';
import tools from './data/tools.js';

/**
 * Returns the available tool ids
 * @returns {Object}
 */

const getToolIds = memoize(() =>
  tools.reduce((list, tool) => {
    list[tool.id] = tool.value;
    return list;
  }, {})
);

const NEW_TAB_TOOLS = [
  getToolIds().url,
  getToolIds().instructorfile,
  getToolIds().staticfile,
];

/**
 * Returns true if the passed in tool type should be launched in a new tab
 * @param {String} tool
 * @returns {Boolean}
 */
const isTabToolLaunch = (tool) => NEW_TAB_TOOLS.includes(tool);

/**
 * Returns the corresponding tool
 * @param {String} tool
 * @returns {Object}
 */
function getTool(tool) {
  return tools.find((x) => x.value === tool) || null;
}

/**
 * Determines whether a tool is local launch or not
 * @param {String} tool
 * @returns {Boolean}
 */
const isLocalToolLaunch = (tool) => {
  const selectedTool = getTool(tool);
  return selectedTool ? selectedTool.isLocalToolLaunch : null;
};

const toolDefaultNames = {
  url: 'Unnamed URL',
  writing: 'Untitled Writing Activity',
  assessment: 'Unnamed Assessment',
  default: 'Unnamed Activity',
};

/**
 * Returns the default assignment name for the passed in tool
 * @param {String} tool
 * @returns {String}
 */
const getDefaultToolName = (tool) =>
  toolDefaultNames[tool] || toolDefaultNames.default;

/**
 * Returns true if the passed in assignment name is the default name for the passed in tool
 * @param {String} name
 * @param {String} tool
 * @returns {Boolean}
 */
const isUnnamed = (name, tool) => {
  if (name) {
    const defaultToolName = getDefaultToolName(tool);
    const defaultName = name
      .toLowerCase()
      .startsWith(defaultToolName.toLowerCase());
    return defaultName;
  }
  return true;
};

/**
 * Returns the default assignment name and date for the passed in tool
 * @param {String} name
 * @param {String} createdAt
 * @returns {String}
 */
const getUnnamedText = (tool, createdAt) =>
  `${getDefaultToolName(tool)} ${moment(createdAt).format('M/D/YYYY')}`;

/**
 * Returns the assignment name or default assignment name if there isn't one
 * @param {String} name
 * @param {String} tool
 * @param {String} createdAt
 * @returns {String}
 */
const formatName = (name, tool, createdAt) =>
  isUnnamed(name, tool) ? getUnnamedText(tool, createdAt) : name;

export { getToolIds, isLocalToolLaunch, isTabToolLaunch, formatName };
